import { Dialog } from '@mui/material';
import { isNotNil } from 'ramda';
import React, { useCallback, useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { applicationSlice } from '../../redux/ApplicationSlice';
import usePrevious from '../../hooks/ui/usePrevious';
import { ApplicationActions, RobotActions } from '../../utils/constants';

// System dialogs
import AboutDialog from './System/AboutDialog';
import AcknowledgementDialog from './System/AcknowledgementDialog';
import BluetoothWarningDialog from './System/BluetoothWarningDialog';
import UpdateConfirmationDialog from './System/UpdateConfirmationDialog';

// Robot dialogs
import ConnectionProgressionDialog from './Robot/ConnectionProgressionDialog';
import BeamBreakCheckDialog from './Robot/ElectronicCheckDialogs/BeamBreakCheckDialog';
import BEEAdaptorCheckDialog from './Robot/ElectronicCheckDialogs/BEEAdaptorCheckDialog';
import DistanceSensorCheckDialog from './Robot/ElectronicCheckDialogs/DistanceSensorCheckDialog';
import MagneticSwitchCheckDialog from './Robot/ElectronicCheckDialogs/MagneticSwitchCheckDialog';
import MotorCheckDialog from './Robot/ElectronicCheckDialogs/MotorCheckDialog';
import RobotConnectInfoDialog from './Robot/RobotConnectInfoDialog';
import RobotSerialConnectionDialog from './Robot/RobotSerialConnectionDialog';
import RobotTemplateDialog from './Robot/RobotTemplate/RobotTemplateDialog';

// Firmware dialogs
import { FirmwareUpdateRebootingDialog, UnsupportedFirmwareDialog } from './ErrorDialogs';
import CriticalFirmwareUpdateDialog from './Firmware/CriticalFirmwareUpdateDialog';
import FirmwareStatusDialog from './Firmware/FirmwareStatusDialog';
import FirmwareUpdatingDialog from './Firmware/FirmwareUpdatingDialog';
import OptionalFirmwareUpdateDialog from './Firmware/OptionalFirmwareUpdateDialog';

// Project dialogs
import DeleteProjectDialog from './Project/DeleteProjectDialog';
import FieldSelectionDialog from './Project/FieldSelectionDialog';
import ProjectNameDialog from './Project/ProjectNameDialog';
import ProjectSavedDialog from './Project/ProjectSavedDialog';
import UploadFileDialog from './Project/UploadFileDialog';
import LoadFromSwerveDialog from './Project/LoadFromSwerveDialog';

// ShareCode dialogs
import CreateShareCodeDialog from './ShareCode/CreateShareCodeDialog';
import EnterCollaboratorPinDialog from './ShareCode/EnterCollaboratorPinDialog';
import OpenShareCodeDialog from './ShareCode/OpenShareCodeDialog';
import RemoveCompleteShareCodeDialog from './ShareCode/RemoveCompleteShareCodeDialog';
import RemoveShareCodeDialog from './ShareCode/RemoveShareCodeDialog';
import ShareCodeInfoDialog from './ShareCode/ShareCodeInfoDialog';

// Code dialogs
import CodeValueOutOfRangeDialog from './Code/CodeValueOutOfRangeDialog';
import LinterErrorsDialog from './Code/LinterErrorsDialog';

// Data dialogs
import DULogsDialog from './Data/DULogsDialog';

// Path dialogs
import PathCreationDialog from './Path/PathCreationDialog';

const { Dialogs } = ApplicationActions;

const DialogManger = () => {
  const { connectionType } = useSelector((state) => state.robot);
  const robotState = useSelector((state) => state.robot.robotState);
  const [firmwareBleInstalling, setFirmwareBleInstalling] = useState(false);
  const { dialog, options } = useSelector((state) => state.application.openDialog);
  const openDrawer = useSelector((state) => state.project.openDrawer);

  const dispatch = useDispatch();

  const previousDialog = usePrevious(dialog);

  const preventBackdropClose = useCallback((event) => {
    event.stopPropagation();
  }, []);

  useEffect(() => {
    if (!dialog) return;
    if (dialog === Dialogs.CONNECTION_PROGRESS.dialog) {
      window.addEventListener('click', preventBackdropClose);
    } else if (previousDialog === Dialogs.CONNECTION_PROGRESS.dialog) {
      window.removeEventListener('click', preventBackdropClose);
    }
    return () => {
      window.removeEventListener('click', preventBackdropClose);
    };
  }, [dialog, openDrawer, previousDialog, preventBackdropClose]);

  useEffect(() => {
    if (dialog !== Dialogs.FIRMWARE_STATUS.dialog) {
      setFirmwareBleInstalling(false);
    }
  }, [dialog]);

  const handleClose = useCallback(
    (_event, reason) => {
      const isConnected = robotState === RobotActions.STATE.CONNECTED;
      const preventFirmwareStatusClose =
        dialog === Dialogs.FIRMWARE_STATUS.dialog &&
        connectionType === RobotActions.STATE.CONNECTION_TYPE.BLE &&
        firmwareBleInstalling &&
        isConnected;
      const preventCriticalFirmwareClose = dialog === Dialogs.CRITICAL_FIRMWARE_DIALOG.dialog;
      const preventConnectionProgressClose = dialog === Dialogs.CONNECTION_PROGRESS.dialog;

      if (
        (reason === 'backdropClick' || reason === 'escapeKeyDown') &&
        (dialog === Dialogs.PROJECT_NAME.dialog ||
          dialog === Dialogs.LOAD_FROM_SWERVE.dialog ||
          dialog === Dialogs.MOTOR_CHECK.dialog ||
          dialog === Dialogs.FIRMWARE_UPDATING.dialog ||
          preventConnectionProgressClose ||
          preventCriticalFirmwareClose ||
          preventFirmwareStatusClose)
      ) {
        // Prevent closing on backdrop click or Escape (see disableEscapeKeyDown on Dialog for other cases)
        return;
      }

      if (dialog === Dialogs.LINTER_ERRORS_DIALOG.dialog) {
        dispatch(applicationSlice.actions.clearPendingLinterIgnore());
      }

      dispatch(applicationSlice.actions.onOpenDialog({ dialog: null, options: {} }));
    },
    [connectionType, dialog, dispatch, firmwareBleInstalling, robotState]
  );

  const previousRobotState = usePrevious(robotState);

  return (
    <Dialog
      onClose={handleClose}
      open={isNotNil(dialog)}
      scroll={'paper'}
      fullScreen={options?.fullScreen}
      fullWidth={options?.fullWidth}
      maxWidth={options?.maxWidth}
      disableEscapeKeyDown={options?.disableEscapeKeyDown}
    >
      {
        <>
          {dialog === Dialogs.CONNECT_SERIAL.dialog && <RobotSerialConnectionDialog onClose={handleClose} />}
          {dialog === Dialogs.CONNECT_INFO.dialog && <RobotConnectInfoDialog onClose={handleClose} />}
          {dialog === Dialogs.CONNECTION_PROGRESS.dialog && <ConnectionProgressionDialog open onClose={handleClose} />}

          {dialog === Dialogs.OPTIONAL_FIRMWARE_DIALOG.dialog && <OptionalFirmwareUpdateDialog open onClose={handleClose} />}
          {dialog === Dialogs.CRITICAL_FIRMWARE_DIALOG.dialog && (
            <CriticalFirmwareUpdateDialog onClose={handleClose} robotTypeName={options.robotTypeName} />
          )}
          {dialog === Dialogs.UPDATE_FIRMWARE_CONFIRMATION_DIALOG.dialog && <UpdateConfirmationDialog open onClose={handleClose} />}

          {dialog === Dialogs.BLUETOOTH_WARNING_DIALOG.dialog && <BluetoothWarningDialog open onClose={handleClose} />}
          {dialog === Dialogs.ABOUT_DIALOG.dialog && <AboutDialog open onClose={handleClose} />}
          {dialog === Dialogs.ACKNOWLEDGMENTS_DIALOG.dialog && <AcknowledgementDialog open onClose={handleClose} />}

          {dialog === Dialogs.V2_FIRMWARE_OUTDATED.dialog && (
            <UnsupportedFirmwareDialog onClose={handleClose} robotTypeName={options.robotTypeName} />
          )}
          {dialog === Dialogs.FIRMWARE_UPDATE_REBOOTING.dialog && <FirmwareUpdateRebootingDialog onClose={handleClose} />}
          {dialog === Dialogs.FIRMWARE_UPDATING.dialog && <FirmwareUpdatingDialog robotTypeName={options.robotTypeName} />}
          {dialog === Dialogs.FIRMWARE_STATUS.dialog && (
            <FirmwareStatusDialog
              onClose={handleClose}
              robotTypeName={options.robotTypeName}
              bleInstalling={firmwareBleInstalling}
              onBleInstallingChange={setFirmwareBleInstalling}
            />
          )}
          {dialog === Dialogs.PROJECT_NAME.dialog && <ProjectNameDialog open onClose={handleClose} />}
          {dialog === Dialogs.PROJECT_SAVED.dialog && <ProjectSavedDialog projectName={options.projectName} open onClose={handleClose} />}
          {dialog === Dialogs.ROBOT_TEMPLATE.dialog && <RobotTemplateDialog open onClose={handleClose} />}
          {dialog === Dialogs.DELETE_PROJECT.dialog && <DeleteProjectDialog open onClose={handleClose} />}
          {dialog === Dialogs.UPLOAD_FILE.dialog && <UploadFileDialog open onClose={handleClose} />}
          {dialog === Dialogs.LOAD_FROM_SWERVE.dialog && <LoadFromSwerveDialog open onClose={handleClose} />}
          {dialog === Dialogs.DU_LOGS.dialog && <DULogsDialog open onClose={handleClose} />}
          {dialog === Dialogs.CODE_VALUE_OUT_OF_RANGE.dialog && (
            <CodeValueOutOfRangeDialog header={options.header} label={options.label} min={options.min} max={options.max} open onClose={handleClose} />
          )}
          {dialog === Dialogs.LINTER_ERRORS_DIALOG.dialog && (
            <LinterErrorsDialog
              ignoreAction={options.linterIgnoreAction ?? 'deploy'}
              onClose={handleClose}
              onIgnoreAndDeploy={() => {
                dispatch(applicationSlice.actions.confirmPendingLinterIgnore());
                dispatch(applicationSlice.actions.onOpenDialog({ dialog: null, options: {} }));
              }}
            />
          )}
          {dialog === Dialogs.PATH_CREATION.dialog && <PathCreationDialog open onClose={handleClose} />}
          {dialog === Dialogs.FIELD_SELECTION.dialog && <FieldSelectionDialog onClose={handleClose} />}

          {dialog === Dialogs.SHARE_CODE_INFO.dialog && (
            <ShareCodeInfoDialog project={options.project} newShareCode={options.newShareCode} open onClose={handleClose} />
          )}
          {dialog === Dialogs.OPEN_SHARE_CODE.dialog && <OpenShareCodeDialog open onClose={handleClose} />}
          {dialog === Dialogs.EDIT_PIN_SHARE_CODE.dialog && <EnterCollaboratorPinDialog open onClose={handleClose} />}
          {dialog === Dialogs.CREATE_SHARE_CODE.dialog && <CreateShareCodeDialog project={options.project} open onClose={handleClose} />}
          {dialog === Dialogs.REMOVE_SHARE_CODE.dialog && <RemoveShareCodeDialog project={options.project} open onClose={handleClose} />}
          {dialog === Dialogs.REMOVE_COMPLETE_SHARE_CODE.dialog && (
            <RemoveCompleteShareCodeDialog project={options.project} open onClose={handleClose} />
          )}

          {dialog === Dialogs.BEAM_BREAK_CHECK.dialog && <BeamBreakCheckDialog open port={options?.port} onClose={handleClose} />}
          {dialog === Dialogs.DISTANCE_SENSOR_CHECK.dialog && <DistanceSensorCheckDialog open port={options?.port} onClose={handleClose} />}
          {dialog === Dialogs.MAGNETIC_SWITCH_CHECK.dialog && <MagneticSwitchCheckDialog open port={options?.port} onClose={handleClose} />}
          {dialog === Dialogs.MOTOR_CHECK.dialog && (
            <MotorCheckDialog open port={options?.port} electronicType={options?.electronicType} onClose={handleClose} />
          )}
          {dialog === Dialogs.BPE_ADAPTOR_CHECK.dialog && <BEEAdaptorCheckDialog open port={options?.port} onClose={handleClose} />}
        </>
      }
    </Dialog>
  );
};

export default DialogManger;
